import { observable, computed, action, makeObservable } from 'mobx';

import type { Optional } from 'chaika-common-types';
import { PAGE_ELEMENT_ID } from '@core/Page/constants';
import { assertIsDefined } from '@utils/assertIsDefined';

const tableSize = 1024;
const mobileSize = 768;

export type OnScrollFunction = (prevPosition: number, nextPosition: number) => void;
export type FunctionWithoutParams = () => void;

export class ViewPortState {
    public static Name = 'viewPort' as const;

    @observable public innerWidth: number;
    @observable public scrollY = 0;
    @observable public scrollX = 0;
    @observable public isOnScrollUp: boolean = false;
    @observable protected onScrollX: Optional<OnScrollFunction>;
    @observable protected onScrollY: Optional<OnScrollFunction>;
    @observable protected onPageEnd: Optional<FunctionWithoutParams>;
    @observable protected onPageRightEnd: Optional<FunctionWithoutParams>;

    protected pageContent!: HTMLElement;

    constructor() {
        makeObservable(this);

        this.innerWidth = window.innerWidth;

        window.addEventListener('resize', this.onResize);

        window.addEventListener('beforeunload', () => {
            window.removeEventListener('resize', this.onResize);
        });
    }

    @computed
    public get isTableSize(): boolean {
        return this.innerWidth <= tableSize;
    }

    public get pageHeight(): number {
        return this.pageContent.scrollHeight;
    }

    public get pageWidth(): number {
        return this.pageContent?.scrollWidth;
    }

    public get height(): number {
        return this.pageContent.clientHeight;
    }

    public get width(): number {
        return this.pageContent.clientWidth;
    }

    @computed
    public get isMobileSize(): boolean {
        return this.innerWidth <= mobileSize;
    }

    @computed
    public get isSafariMobile(): boolean {
        const userAgent = navigator.userAgent.toLowerCase();

        const isIPhone = userAgent.indexOf('iphone') > -1 && this.isMobileSize;
        const isSafari = userAgent.indexOf('safari') > -1;

        return isIPhone && isSafari;
    }

    @action
    public setPageHtmlElement(page: HTMLElement) {
        this.pageContent = page;
    }

    @action
    public updatePageHtmlElement() {
        const pageContent = document.getElementById(PAGE_ELEMENT_ID);
        assertIsDefined(pageContent, 'Page content element is rendered by Page before its children mount');
        this.pageContent = pageContent;
    }

    @action
    public setOnScrollXHandler(onScrollX: OnScrollFunction): void {
        this.onScrollX = onScrollX;
    }

    @action
    public setOnScrollYHandler(onScrollY: OnScrollFunction): void {
        this.onScrollY = onScrollY;
    }

    @action
    public setOnPageEndHandler(onPageEnd?: FunctionWithoutParams): void {
        this.onPageEnd = onPageEnd;
    }

    @action
    public setOnPageRightEndHandler(onPageRightEnd: FunctionWithoutParams): void {
        this.onPageRightEnd = onPageRightEnd;
    }

    @action
    public resetScrollHandlers(): void {
        this.onScrollX = undefined;
        this.onScrollY = undefined;
        this.onPageEnd = undefined;
        this.onPageRightEnd = undefined;
    }

    @action.bound
    public onContentScroll(): void {
        this.isOnScrollUp = window.scrollY < this.scrollY;

        this.scrollY = window.scrollY;
    }

    @action.bound
    public setScrollPosition(scrollY: number, scrollX: number): void {
        this.isOnScrollUp = scrollY < this.scrollY;

        if (this.onScrollX && scrollX !== this.scrollX) {
            this.onScrollX(this.scrollX, scrollX);
        }

        if (this.onScrollY && scrollY !== this.scrollY) {
            this.onScrollY(this.scrollY, scrollY);
        }

        if (this.onPageEnd && this.pageHeight - this.height - scrollY < 120) {
            this.onPageEnd();
        }

        if (this.onPageRightEnd && this.pageWidth - this.width - scrollX < 50) {
            this.onPageRightEnd();
        }

        this.scrollY = scrollY;
        this.scrollX = scrollX;
    }

    @action.bound
    private onResize(): void {
        this.innerWidth = window.innerWidth;
    }
}

export const viewPort = new ViewPortState();
